import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { enrichMealNutrients } from '@/hooks/useMeals';

// ============================================
// ТИПЫ
// ============================================
export interface HistoryItem {
  name: string;
  avg_weight_g: number;
  avg_calories: number;
  avg_protein_g: number;
  avg_fat_g: number;
  avg_carbs_g: number;
  use_count: number;
  last_used: string;
  avg_fiber_g: number;
  avg_calcium_mg: number;
  avg_iron_mg: number;
  avg_magnesium_mg: number;
  avg_potassium_mg: number;
}

export interface HistoryMeal {
  meal_id: string;
  meal_type: string;
  consumed_at: string;
  total_calories: number;
  total_protein_g: number;
  total_fat_g: number;
  total_carbs_g: number;
  items_count: number;
  item_names: string[];
  days_ago: number;
  preview_text: string;
}

// ============================================
// ХУК ИСТОРИИ
// ============================================

/**
 * Хук для загрузки истории продуктов и приёмов юзера.
 * 
 * Возвращает:
 * - topItems — самые часто используемые продукты (30 дней)
 * - recentMeals — последние приёмы пищи (7 дней)
 * 
 * @example
 * const { topItems, recentMeals, loading } = useFoodHistory();
 */
export function useFoodHistory() {
  const [topItems, setTopItems] = useState<HistoryItem[]>([]);
  const [recentMeals, setRecentMeals] = useState<HistoryMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      
      // Параллельная загрузка
      const [itemsResult, mealsResult] = await Promise.all([
        supabase.rpc('get_user_top_items', { 
          p_user_id: user.id,
          p_limit: 30,
          p_days: 30 
        }),
        supabase.rpc('get_user_recent_meals', { 
          p_user_id: user.id,
          p_limit: 15,
          p_days: 7 
        }),
      ]);
      
      if (itemsResult.error) {
        console.error('[useFoodHistory] top items error:', itemsResult.error);
      } else {
        setTopItems((itemsResult.data || []) as HistoryItem[]);
      }
      
      if (mealsResult.error) {
        console.error('[useFoodHistory] recent meals error:', mealsResult.error);
      } else {
        setRecentMeals((mealsResult.data || []) as HistoryMeal[]);
      }
    } catch (e: any) {
      console.error('[useFoodHistory] error:', e);
      setError(e.message || 'Не удалось загрузить историю');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    refetch();
  }, [refetch]);
  
  return {
    topItems,
    recentMeals,
    loading,
    error,
    refetch,
  };
}

// ============================================
// ОПЕРАЦИИ
// ============================================

/**
 * Копирует целый приём пищи в выбранный день (по умолчанию — сегодня, сейчас).
 * Без вызова AI — мгновенно.
 *
 * consumedAt уходит в саму функцию (миграция 060), а не правится вторым запросом
 * после вставки: иначе запись на завтра успевала засчитаться в серию сегодня.
 */
export async function copyMealToToday(
  sourceMealId: string,
  newMealType?: string,
  consumedAt?: Date,
): Promise<{ success: boolean; meal_id?: string; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'not_authenticated' };
    }
    
    const { data, error } = await supabase.rpc('copy_meal_to_today', {
      p_user_id: user.id,
      p_source_meal_id: sourceMealId,
      p_meal_type: newMealType ?? null,
      p_consumed_at: consumedAt ? consumedAt.toISOString() : null,
    });
    
    if (error) {
      console.error('[copyMealToToday] error:', error);
      return { success: false, error: error.message };
    }
    
    // Приём создаётся серверной RPC, мимо клиентского createMeal. Если исходный
    // приём был обогащён, копия унаследует микронутриенты и вызов ничего не сделает
    // (внутри стоит проверка на уже заполненные) — но если нет, дозаполнит.
    const mealId = data as string;
    if (mealId) {
      enrichMealNutrients(mealId).catch((e) =>
        console.error('[copyMealToToday] enrich failed:', e),
      );
    }

    return { success: true, meal_id: mealId };
  } catch (e: any) {
    console.error('[copyMealToToday] failed:', e);
    return { success: false, error: e.message };
  }
}

/**
 * Поиск id meal_item по названию (последнее использование).
 * Нужно чтобы потом скопировать продукт через add_item_from_history.
 * 
 * Использует RPC функцию (т.к. в meal_items нет created_at,
 * сортировка идёт через JOIN с meals.consumed_at).
 */
export async function findLatestItemIdByName(itemName: string): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data, error } = await supabase.rpc('find_latest_item_id_by_name', {
      p_user_id: user.id,
      p_item_name: itemName.trim(),
    });
    
    if (error) {
      console.error('[findLatestItemIdByName] error:', error);
      return null;
    }
    
    return (data as string) || null;
  } catch (e) {
    console.error('[findLatestItemIdByName] failed:', e);
    return null;
  }
}

/**
 * Добавляет ОДИН продукт из истории.
 * Создаёт новый отдельный meal или добавляет в существующий.
 * КБЖУ пересчитывается пропорционально новому весу.
 */
export async function addItemFromHistory(params: {
  sourceItemId: string;
  newWeightG: number;
  targetMealId?: string;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  /** День и время нового приёма; без него — сейчас. Учитывается только когда создаётся новый приём. */
  consumedAt?: Date;
}): Promise<{ success: boolean; meal_id?: string; item_id?: string; created_new_meal?: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'not_authenticated' };
    }
    
    const { data, error } = await supabase.rpc('add_item_from_history', {
      p_user_id: user.id,
      p_source_item_id: params.sourceItemId,
      p_new_weight_g: params.newWeightG,
      p_target_meal_id: params.targetMealId ?? null,
      p_meal_type: params.mealType ?? 'snack',
      p_consumed_at: params.consumedAt ? params.consumedAt.toISOString() : null,
    });
    
    if (error) {
      console.error('[addItemFromHistory] error:', error);
      return { success: false, error: error.message };
    }
    
    const result = data as any;

    // Тот же случай: продукт кладёт серверная RPC, обогащение надо дозвать
    if (result?.meal_id) {
      enrichMealNutrients(result.meal_id).catch((e) =>
        console.error('[addItemFromHistory] enrich failed:', e),
      );
    }

    return {
      success: true,
      meal_id: result.meal_id,
      item_id: result.item_id,
      created_new_meal: result.created_new_meal 
    };
  } catch (e: any) {
    console.error('[addItemFromHistory] failed:', e);
    return { success: false, error: e.message };
  }
}
